import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { CategoryPerformanceDto } from '../../services/api';

interface CategoryPerformanceChartProps {
  data: CategoryPerformanceDto[];
}

// Color palette for categories
const CHART_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#ec4899', // pink
  '#6b7280', // gray
];

export function CategoryPerformanceChart({ data }: CategoryPerformanceChartProps) {
  // Prepare data for pie chart (usage percentage)
  const pieData = data.map((category, index) => ({
    name: category.categoryName,
    value: category.usagePercentage,
    color: category.categoryColor || CHART_COLORS[index % CHART_COLORS.length],
    totalUses: category.totalUses,
    promptCount: category.promptCount
  }));

  // Prepare data for bar chart (performance metrics)
  const barData = data.map((category, index) => ({
    name: category.categoryName.length > 10 
      ? category.categoryName.substring(0, 10) + '...'
      : category.categoryName,
    fullName: category.categoryName,
    prompts: category.promptCount,
    uses: category.totalUses,
    likes: category.totalLikes,
    rating: category.averageRating,
    color: category.categoryColor || CHART_COLORS[index % CHART_COLORS.length]
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.dataKey}: ${entry.value.toLocaleString()}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium">{data.name}</p>
          <p>{`Usage: ${data.value.toFixed(1)}%`}</p>
          <p>{`Total Uses: ${data.totalUses.toLocaleString()}`}</p>
          <p>{`Prompts: ${data.promptCount}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Usage Distribution Pie Chart */}
      <div>
        <h4 className="text-sm font-medium text-muted-foreground mb-3">Usage Distribution</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                formatter={(value) => value.length > 15 ? value.substring(0, 15) + '...' : value}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Metrics Bar Chart */}
      <div>
        <h4 className="text-sm font-medium text-muted-foreground mb-3">Performance Metrics</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="name" 
                stroke="#64748b"
                fontSize={11}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="uses" 
                fill="#3b82f6" 
                name="Uses"
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category Summary Table */}
      <div>
        <h4 className="text-sm font-medium text-muted-foreground mb-3">Category Summary</h4>
        <div className="space-y-2">
          {data.map((category, index) => (
            <div 
              key={category.categoryId}
              className="flex items-center justify-between p-2 rounded border"
            >
              <div className="flex items-center space-x-3">
                <div 
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: category.categoryColor || CHART_COLORS[index % CHART_COLORS.length] }}
                />
                <span className="text-sm font-medium">{category.categoryName}</span>
              </div>
              
              <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                <span>{category.promptCount} prompts</span>
                <span>{category.totalUses.toLocaleString()} uses</span>
                <span>{category.totalLikes.toLocaleString()} likes</span>
                {category.averageRating > 0 ? (
                  <span>★ {category.averageRating.toFixed(1)}</span>
                ) : (
                  <span className="text-muted-foreground opacity-40">★ —</span>
                )}
                <span className={`font-medium ${category.growthRate?.startsWith('-') ? 'text-red-600' : 'text-green-600'}`}>{category.growthRate}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}